const EDGE_PADDING = 8;
const GAP = 5;
// Above Bootstrap's tooltip layer, which the calendar also renders into.
const OVERLAY_Z_INDEX = '1070';

const overlayTeardowns = new WeakMap<HTMLElement, () => void>();

export interface OverlayRect {
    readonly top: number;
    readonly left: number;
    readonly width: number;
    readonly height: number;
}

export interface OverlayPoint {
    readonly top: number;
    readonly left: number;
}

export interface OverlaySize {
    readonly width: number;
    readonly height: number;
}

/**
 * The on-screen region, in layout-viewport coordinates.
 *
 * Pinch-zoom and the iOS keyboard shrink the visual viewport while
 * getBoundingClientRect() keeps reporting layout coordinates, so the offsets
 * are needed to clamp against what is actually on screen.
 */
export interface OverlayViewport {
    readonly width: number;
    readonly height: number;
    readonly offsetLeft: number;
    readonly offsetTop: number;
}

export interface OverlayPlacement {
    readonly top: number;
    readonly left: number;
    readonly position: 'absolute';
}

export const bindScrollSafeOverlay = (
    trigger: HTMLElement,
    getOverlay: () => HTMLElement | undefined,
    getTrigger: () => HTMLElement = (): HTMLElement => trigger,
): void => {
    unbindScrollSafeOverlay(trigger);

    const controller = new AbortController();
    const { signal } = controller;
    let frame: number | undefined;
    let observer: MutationObserver | undefined;

    const teardown = (): void => {
        if (frame !== undefined) {
            cancelAnimationFrame(frame);
            frame = undefined;
        }
        observer?.disconnect();
        observer = undefined;
        controller.abort();
        overlayTeardowns.delete(trigger);
    };

    const place = (overlay: HTMLElement): void => {
        // Disconnect so our own style writes do not retrigger the observer.
        observer?.disconnect();
        const liveTrigger = getTrigger();
        applyOverlayPlacement(overlay, liveTrigger.isConnected ? liveTrigger : trigger);
        observer?.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    };

    // Repositioning reads layout then writes style, so coalesce a burst of
    // scroll events (and FC's own style mutations) into one pass per frame.
    const reposition = (): void => {
        if (frame !== undefined) {
            return;
        }
        frame = requestAnimationFrame(() => {
            frame = undefined;
            const overlay = getOverlay();
            if (!overlay) {
                // Dismissed by Escape, an outside tap, or a re-render. Nothing
                // re-attaches this overlay, so stop tracking it.
                teardown();
                return;
            }
            place(overlay);
        });
    };

    observer = new MutationObserver(reposition);

    window.addEventListener('scroll', reposition, { capture: true, passive: true, signal });
    window.addEventListener('resize', reposition, { passive: true, signal });
    window.visualViewport?.addEventListener('scroll', reposition, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', reposition, { passive: true, signal });

    overlayTeardowns.set(trigger, teardown);

    const overlay = getOverlay();
    if (overlay) {
        place(overlay);
        // FC's MorePopover.componentDidMount updateSize can run after the first
        // paint and overwrite top/left. One extra frame lets us win that race.
        requestAnimationFrame(reposition);
    }
};

export const unbindScrollSafeOverlay = (trigger: HTMLElement): void => {
    overlayTeardowns.get(trigger)?.();
};

export const applyOverlayPlacement = (overlay: HTMLElement, trigger: HTMLElement): void => {
    // position:fixed is viewport-relative, matching getBoundingClientRect.
    // FullCalendar portals the more-popover with position:absolute; after scroll,
    // html/body offsetParent vs the initial containing block disagree, which is
    // CRO-14575 (popover pinned near the document origin, ~scrollY from the control).
    overlay.style.setProperty('position', 'fixed', 'important');
    overlay.style.right = 'auto';
    overlay.style.bottom = 'auto';
    overlay.style.margin = '0';
    overlay.style.transform = 'none';
    overlay.style.zIndex = OVERLAY_Z_INDEX;

    const triggerRect: DOMRect = trigger.getBoundingClientRect();
    const overlayRect: DOMRect = overlay.getBoundingClientRect();
    const placed: OverlayPlacement = overlayPosition(
        {
            top: triggerRect.top,
            left: triggerRect.left,
            width: triggerRect.width,
            height: triggerRect.height,
        },
        {
            width: overlay.offsetWidth || overlayRect.width,
            height: overlay.offsetHeight || overlayRect.height,
        },
        readOverlayViewport(),
        { top: 0, left: 0 },
    );

    overlay.style.top = `${placed.top}px`;
    overlay.style.left = `${placed.left}px`;
};

/**
 * Place the overlay below its trigger, flipping above when it would overflow
 * the visible region and clamping so it stays on screen.
 *
 * Works in layout-viewport coordinates (what getBoundingClientRect reports) and
 * converts into the containing block last, so a scrolled or offset ancestor
 * cannot pin the overlay at the page origin (CRO-14575).
 */
export const overlayPosition = (
    trigger: OverlayRect,
    overlay: OverlaySize,
    viewport: OverlayViewport,
    containing: OverlayPoint,
): OverlayPlacement => {
    const minTop: number = viewport.offsetTop + EDGE_PADDING;
    const maxBottom: number = viewport.offsetTop + viewport.height - EDGE_PADDING;
    const minLeft: number = viewport.offsetLeft + EDGE_PADDING;
    const maxRight: number = viewport.offsetLeft + viewport.width - EDGE_PADDING;

    const below: number = trigger.top + trigger.height + GAP;
    const above: number = trigger.top - overlay.height - GAP;
    // Only flip when the overlay fits above; otherwise the clamp drags it back
    // down over the trigger.
    const flipAbove: boolean = below + overlay.height > maxBottom && above >= minTop;

    const top: number = clamp(flipAbove ? above : below, minTop, Math.max(minTop, maxBottom - overlay.height));
    const left: number = clamp(
        trigger.left + trigger.width / 2 - overlay.width / 2,
        minLeft,
        Math.max(minLeft, maxRight - overlay.width),
    );

    return { top: top - containing.top, left: left - containing.left, position: 'absolute' };
};

export const readOverlayViewport = (): OverlayViewport => {
    const visual: VisualViewport | null = window.visualViewport;
    if (!visual) {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
            offsetLeft: 0,
            offsetTop: 0,
        };
    }

    return {
        width: visual.width,
        height: visual.height,
        offsetLeft: visual.offsetLeft,
        offsetTop: visual.offsetTop,
    };
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);
